import { useMemo } from 'react';
import { MonoLabel, Skeleton, Tag } from '../primitives';
import { PickOrCreate } from '../primitives/PickOrCreate';
import { useDocumentProjects } from '../../lib/projects/useDocumentProjects';
import { useOrgProjects } from '../../lib/projects/useOrgProjects';
import { useProjectMembership } from '../../lib/projects/useProjectMembership';

export interface DocumentProjectsSectionProps {
  documentId: string;
  orgId: string;
  editable: boolean;
  onError: (message: string) => void;
}

/**
 * "Projects" section for the document editor's Properties tab. The reverse of
 * the project canvas's documents list: a document may belong to several
 * projects (the `project_documents` many-to-many). Linked projects render as
 * dismissible `<Tag>` chips — mirroring the `multi_select` AttributeRow so the
 * section reads like the other multi-value properties (DRI / QA lead). The
 * pill picker adds one more (already-linked projects are excluded). Mounted as
 * a sibling of <PropertiesPanel> so it shows even for document types with zero
 * custom attributes.
 */
export function DocumentProjectsSection({
  documentId,
  orgId,
  editable,
  onError
}: DocumentProjectsSectionProps) {
  const { load, reload } = useDocumentProjects(documentId);
  const { projects: orgProjects } = useOrgProjects(orgId);
  const { linkDocument, unlinkDocument } = useProjectMembership();

  const linkedIds = useMemo(
    () => new Set(load.kind === 'ready' ? load.projects.map((p) => p.id) : []),
    [load]
  );

  const addOptions = useMemo(
    () =>
      orgProjects
        .filter((p) => !linkedIds.has(p.id))
        .map((p) => ({ id: p.id, label: p.name })),
    [orgProjects, linkedIds]
  );

  const handleAdd = async (projectId: string) => {
    const ok = await linkDocument(orgId, projectId, documentId);
    if (ok) reload();
    else onError('Could not link the document to that project.');
  };

  const handleRemove = async (projectId: string) => {
    const ok = await unlinkDocument(projectId, documentId);
    if (ok) reload();
    else onError('Could not remove the document from that project.');
  };

  const count = load.kind === 'ready' ? load.projects.length : undefined;

  return (
    <section
      className="dev2-projects-naked"
      data-testid="document-editor-v2-projects"
      data-state={load.kind}
      data-row-count={count}
      aria-label="Linked projects"
    >
      <div className="de-row" data-attr-kind="projects">
        <MonoLabel size="sm" tracking="wider" className="de-row-label">
          Projects
        </MonoLabel>
        <div className="de-row-value de-row-multi">
          {load.kind === 'loading' ? (
            <Skeleton width="100%" height={28} />
          ) : load.kind === 'error' ? (
            <div className="dev2-projects-error">{load.message}</div>
          ) : load.projects.length === 0 ? (
            <p
              className="dev2-projects-empty"
              data-testid="document-editor-v2-projects-empty"
            >
              Not linked to any project yet.
            </p>
          ) : (
            <div
              className="de-multi-strip"
              data-testid="document-editor-v2-projects-strip"
            >
              {load.projects.map((p) => (
                <Tag
                  key={p.id}
                  dismissible
                  disabled={!editable}
                  dismissLabel={`Remove from ${p.name}`}
                  onDismiss={() => void handleRemove(p.id)}
                  data-testid={`document-editor-v2-projects-row-${p.id}`}
                >
                  {p.name}
                </Tag>
              ))}
            </div>
          )}

          {editable ? (
            <PickOrCreate
              id="document-editor-v2-projects-add"
              label="Add to project"
              options={addOptions}
              value={null}
              disabled={addOptions.length === 0}
              triggerVariant="pill"
              pillLabel="+ Add to project"
              pillMuted
              data-testid="document-editor-v2-projects-add"
              onPick={(picked) => {
                if (picked) void handleAdd(picked.id);
              }}
              onCreate={async () => null}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}
